import { IFootballConfig, ITeamSquadConfig, ITeamSquadItem } from '../../../../packages/types';
import { removeUndefinedPropertiesFromObject, LinkCreationService } from '../../../../packages/utils';
import { INormalizedTeamSquadGridItem, TeamPlayerStat } from '../types';
import { assetsPlaceholder } from '../../../../packages/constants/assetsPlaceholder';
import { NOT_AVAILABLE } from '../../../../packages/constants/notAvailable';
import { TARGET_SELF, TARGET_BLANK } from '../../../../packages/constants/windowTarget';
import { PLAYER_ENTITY } from '../../../../packages/constants/entityTypes';
import { getTeamPlayerStatValue } from '../utils/getTeamPlayerStatValue';
import { camelToSnakeCase, getLabelValue, toSentenceCase } from '@sportal365/smp-widget-utils';

interface INormalizeTeamSquadItemDataArgs {
  item: ITeamSquadItem;
  dataEntityLinks?: IFootballConfig['dataEntityLinks'];
  dataLabels?: ITeamSquadConfig['dataLabels'];
  dataTeamNameType?: IFootballConfig['dataTeamNameType'];
  playerStats?: TeamPlayerStat[];
  goalkeeperStats?: TeamPlayerStat[];
  tooltips?: string[];
}

export const normalizeTeamSquadGridItem = ({
  item,
  dataEntityLinks,
  dataLabels,
  playerStats,
  goalkeeperStats,
  tooltips,
}: INormalizeTeamSquadItemDataArgs): INormalizedTeamSquadGridItem | undefined => {
  if (!item) return;

  const linkService = dataEntityLinks && new LinkCreationService(dataEntityLinks);

  const playerAge = Math.floor((new Date().valueOf() - new Date(item.player.birthdate).getTime()) / 3.15576e10);

  // Use goalkeeperStats if the player's position is 'keeper', otherwise use playerStats
  const stats = item.position === 'keeper' ? goalkeeperStats : playerStats?.filter((stat) => stat !== 'conceded');

  // Filter tooltips to match the filtered stats
  const tooltipsValues = item.position === 'keeper' ? tooltips : tooltips?.filter((_, index) => playerStats?.[index] !== 'conceded');

  return removeUndefinedPropertiesFromObject({
    player: {
      id: item?.player?.id,
      name: item.player ? item.player.name : NOT_AVAILABLE,
      playerImage: item.player.thumb || assetsPlaceholder.participantPlayer,
      link: linkService?.createEntityUrl(String(item?.player?.id), PLAYER_ENTITY, '', item?.player?.slug) ?? '#',
      urlTarget: dataEntityLinks?.configuration?.newWindow ? TARGET_BLANK : TARGET_SELF,
      countryName: item?.player?.country?.name ? item.player.country.name : NOT_AVAILABLE,
      countryFlag: item?.player?.country?.url_flag ? item.player.country.url_flag : assetsPlaceholder.country,
      shirtNumber: item?.shirtNumber,
      stats:
        stats?.map((stat, index) => {
          return {
            name: stat,
            value: getTeamPlayerStatValue(stat, item, playerAge),
            tooltip: tooltipsValues
              ? tooltipsValues[index]
              : getLabelValue(dataLabels && dataLabels[camelToSnakeCase(stat)], toSentenceCase(camelToSnakeCase(stat))),
          };
        }) ?? [],
    },
  });
};
